<script setup lang="ts">
import VueTailwindDatepicker from 'vue-tailwind-datepicker'

import { SlidersHorizontal } from 'lucide-vue-next'

const { t } = useI18n()

const props = defineProps({
  dateRangeValue: {
    type: Object,
    default: () => ({})
  },
  displayDateRangePicker: {
    type: Boolean,
    default: false
  },
  displayFilters: {
    type: Boolean,
    default: false
  },
  displayStoreSelector: {
    type: Boolean,
    default: false
  },
  rangePickerPlaceholder: {
    type: String,
    default: 'ui.dateRangePlaceholder'
  },

  storesList: {
    type: Array,
    default: () => []
  }
})
const emit = defineEmits([
  'update:date-range-value',
  'storeSelected',
  'openFilters',
  'validateFilters',
  'cancelFilters',
  'handleFiltersModal'
])
const openFilters = ref(false)

const formatter = ref({
  date: 'DD-MM-YYYY',
  month: 'MMM'
})
const customShortcuts = () => {
  return [
    {
      label: '7 derniers jours',
      atClick: () => {
        const date = new Date()
        return [new Date(date.setDate(date.getDate() - 7)), new Date()]
      }
    },
    {
      label: '30 derniers jours',
      atClick: () => {
        const date = new Date()
        return [new Date(date.setDate(date.getDate() - 30)), new Date()]
      }
    },
    {
      label: '3 derniers mois',
      atClick: () => {
        const date = new Date()
        return [new Date(date.setMonth(date.getMonth() - 3)), new Date()]
      }
    },
    {
      label: '6 derniers mois',
      atClick: () => {
        const date = new Date()
        return [new Date(date.setMonth(date.getMonth() - 6)), new Date()]
      }
    }
  ]
}
const filtersNumber = ref(null)

onMounted(() => {
  filtersNumber.value = Object.values(props).filter((value) => value === true).length
})
const dateRangeChanged = (newDateRangeValue) => {
  emit('update:date-range-value', newDateRangeValue)
}
const validateFilters = () => {
  emit('validateFilters')
  openFilters.value = false
}
const handleFiltersModal = (newState) => {
  openFilters.value = newState
  emit('handleFiltersModal', newState)
}

defineExpose({ handleFiltersModal })
</script>

<template>
  <div class="flex w-full items-start justify-start gap-4 md:w-auto lg:items-end lg:justify-end xl:items-center">
    <DropdownStore
      v-if="displayStoreSelector"
      class="hidden sm:block"
      :display-store-name="false"
      :stores-list="storesList"
      @update-dropdown="$emit('storeSelected', $event)"
    />
    <div v-if="displayDateRangePicker" class="w-full md:w-fit">
      <vue-tailwind-datepicker
        ref="rangeInput"
        :model-value="dateRangeValue"
        class="!min-w-235 cursor-pointer border-input hover:border-primary focus:border-primary focus:ring-0"
        i18n="fr"
        :placeholder="t(rangePickerPlaceholder)"
        :shortcuts="customShortcuts"
        separator=" - "
        :formatter="formatter"
        use-range
        @update:model-value="dateRangeChanged"
      />
    </div>
    <Sheet :open="openFilters" @update:open="handleFiltersModal">
      <SheetTrigger v-if="displayFilters" as-child>
        <Button type="button" variant="outline" class="hidden sm:flex">
          <SlidersHorizontal />
          {{ t('global.filters.filter') }}
        </Button>
      </SheetTrigger>
      <SheetContent class="flex flex-col gap-0 p-0 sm:max-w-md">
        <SheetHeader class="border-b">
          <SheetTitle>
            <slot name="filtersTitle">
              {{ t('global.filters.title') }}
            </slot>
          </SheetTitle>
          <SheetDescription>
            <slot name="filtersDescription" />
          </SheetDescription>
        </SheetHeader>
        <div class="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
          <slot name="filtersContent" />
        </div>
        <SheetFooter class="flex-row justify-end gap-2 border-t">
          <Button variant="outline" @click="handleFiltersModal(false)">
            {{ t('global.filters.buttons.cancel') }}
          </Button>
          <Button variant="default-primary" @click="validateFilters">
            {{ t('global.filters.buttons.submit') }}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
    <slot name="additionalSettings" />
  </div>
</template>
